import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { crearAsientoManual } from "../hooks/useAsientos";
import { CuentaSelectSearch } from "../../catalogo/components/CuentaSelect";
import styles from "../../invoices/styles/addInvoice.module.css";

/*
  Design tokens (del proyecto):
  bg-panel:  #1f2937   bg-input: #344152   border: #364250
  text:      #e5ecf5   placeholder: #8d98a7
  primary:   #f97316   font-size: 14px
*/

const D   = (n) => Number(n || 0);
const fmt = (n) => D(n).toLocaleString("es-CR", { minimumFractionDigits: 2 });

const LINEA_VACIA = () => ({
  _key:        Date.now() + Math.random(),
  cuenta:      null,
  cuentaObj:   null,
  debito:      "",
  credito:     "",
  descripcion: "",
});

export default function AddAsiento() {
  const navigate = useNavigate();
  const today    = new Date().toISOString().split("T")[0];

  const [form,   setForm]   = useState({ fecha: today, concepto: "" });
  const [lineas, setLineas] = useState([LINEA_VACIA(), LINEA_VACIA()]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState("");

  const totalD = lineas.reduce((a, l) => a + D(l.debito),  0);
  const totalC = lineas.reduce((a, l) => a + D(l.credito), 0);
  const diff   = Math.abs(totalD - totalC);
  const ok     = diff < 0.001 && totalD > 0;

  function setLinea(key, field, value) {
    setLineas((p) => p.map((l) => l._key === key ? { ...l, [field]: value } : l));
  }
  function setCuenta(key, id, obj) {
    setLineas((p) => p.map((l) => l._key === key ? { ...l, cuenta: id, cuentaObj: obj } : l));
  }
  function agregarLinea()   { setLineas((p) => [...p, LINEA_VACIA()]); }
  function eliminarLinea(k) {
    setLineas((prev) => {
      if (prev.length <= 2) return prev;
      return prev.filter((l) => l._key !== k);
    });
  }

  function validate() {
    const e = {};
    if (!form.fecha)           e.fecha    = "Requerida.";
    if (!form.concepto.trim()) e.concepto = "Requerido.";
    lineas.forEach((l, i) => {
      if (!l.cuenta)                             e[`l_cuenta_${i}`] = "Seleccioná una cuenta.";
      if (!D(l.debito) && !D(l.credito))        e[`l_monto_${i}`]  = "Ingresá débito o crédito.";
      if (D(l.debito) > 0 && D(l.credito) > 0) e[`l_ambos_${i}`]  = "Solo débito O crédito.";
    });
    if (!ok && !Object.keys(e).some((k) => !k.startsWith("l_")))
      e.balance = `No balanceado — diferencia ₡${fmt(diff)}`;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setApiErr("");
    try {
      await crearAsientoManual({
        fecha:    form.fecha,
        concepto: form.concepto.trim(),
        lineas:   lineas.map((l) => ({
          cuenta:      l.cuenta,
          debito:      D(l.debito)  || 0,
          credito:     D(l.credito) || 0,
          descripcion: l.descripcion || "",
        })),
      });
      navigate("/finanzas/asientos");
    } catch (e) {
      try {
        const p = JSON.parse(e.message);
        setApiErr(p.lineas?.join(", ") || p.non_field_errors?.join(", ") || JSON.stringify(p));
      } catch { setApiErr(e.message); }
    } finally { setSaving(false); }
  }

  /* ── Estilos reutilizables — idénticos a Facturas/Recibos ── */
  const panelStyle = {
    background: "#1f2937",
    border: "1px solid #364250",
    borderRadius: 6,
    overflow: "hidden",
  };

  const inputStyle = (err) => ({
    width: "100%", boxSizing: "border-box",
    padding: "8px 12px",
    background: "#344152",
    border: `1px solid ${err ? "#ef4444" : "#364250"}`,
    borderRadius: 6,
    color: "#e5ecf5",
    fontSize: 14,
    outline: "none",
    colorScheme: "dark",
    transition: "0.3s",
  });

  const labelStyle = {
    display: "block",
    fontSize: 14,
    fontWeight: 500,
    color: "#e5ecf5",
    marginBottom: 4,
  };

  const colHeaderStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "#8d98a7",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    padding: "10px 12px",
  };

  const errTxt = (msg) => msg
    ? <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{msg}</p>
    : null;

  return (
    <>
      {/* Breadcrumb */}
            <span className={styles.inactive}>
              <Link to="/">
                <span className={styles.inactive}>Inicio /</span>
              </Link>
              <Link to="/finanzas/asientos">
                <span className={styles.inactive}> Asientos de Diario /</span>
              </Link>
                <span className={styles.active}> Nuevo Asiento Manual</span>
            </span>

      <h2 style={{ fontSize: 20, fontWeight: 600, color: "#e5ecf5", margin: "12px 0 20px",
        display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          background: "rgba(249,115,22,0.2)", color: "#f97316",
          padding: "6px 8px", borderRadius: 8,
        }}>
          <i className="fa-solid fa-book-open" style={{color:"#f97316"}} />
        </span>
        Nuevo Asiento de Diario
      </h2>



      <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* ── Encabezado ── */}
        <div style={panelStyle}>
          <div style={{
            padding: "12px 16px", borderBottom: "1px solid #364250",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="fa-solid fa-circle-info" style={{ color: "#f97316", fontSize: 13 }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: "#e5ecf5" }}>Encabezado</span>
          </div>
          <div style={{ padding: 16, display: "grid", gridTemplateColumns: "180px 1fr", gap: 14 }}>
            <div>
              <label style={labelStyle}>Fecha <span style={{ color: "#f87171" }}>*</span></label>
              <input type="date" style={inputStyle(errors.fecha)}
                value={form.fecha}
                onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))} />
              {errTxt(errors.fecha)}
            </div>
            <div>
              <label style={labelStyle}>Concepto <span style={{ color: "#f87171" }}>*</span></label>
              <input type="text" style={inputStyle(errors.concepto)}
                placeholder="Ej: Pago de servicios — Enero 2026"
                value={form.concepto}
                onChange={(e) => setForm((p) => ({ ...p, concepto: e.target.value }))} />
              {errTxt(errors.concepto)}
            </div>
          </div>
        </div>

        {/* ── Líneas ── */}
        <div style={{ ...panelStyle, overflow: "visible" }}>
          {/* Encabezado columnas */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(180px,2fr) minmax(120px,1fr) 130px 130px 36px",
            background: "rgba(55,65,81,0.2)",
            borderBottom: "1px solid #364250",
          }}>
            {["Cuenta Contable", "Descripción", "Debe ₡", "Haber ₡", ""].map((h, i) => (
              <span key={i} style={{
                ...colHeaderStyle,
                textAlign: ["Debe ₡", "Haber ₡"].includes(h) ? "right" : "left",
              }}>{h}</span>
            ))}
          </div>

          {/* Filas */}
          {lineas.map((l, i) => {
            const nat = l.cuentaObj?.naturaleza; // "DEUDORA" | "ACREEDORA" | undefined

            // ── Naturaleza — solo guía visual, sin bloqueos ─────────────────
            const sugiereHaber = nat === "ACREEDORA";
            const sugiereDebe  = nat === "DEUDORA";
            const enContraNat  = (sugiereDebe && Number(l.credito) > 0) ||
                                 (sugiereHaber && Number(l.debito)  > 0);

            return (
              <div key={l._key} style={{
                display: "grid",
                gridTemplateColumns: "minmax(180px,2fr) minmax(120px,1fr) 130px 130px 36px",
                gap: 8, padding: "8px 12px",
                background: i % 2 === 0 ? "transparent" : "rgba(55,65,81,0.1)",
                borderBottom: "1px solid #364250",
                alignItems: "start",
                overflow: "visible",
                position: "relative",
                zIndex: lineas.length - i,
              }}>
                {/* Cuenta + badge naturaleza */}
                <div style={{ overflow: "visible" }}>
                  <CuentaSelectSearch
                    value={l.cuenta}
                    onChange={(id, obj) => setCuenta(l._key, id, obj)}
                    placeholder="Buscar cuenta…"
                    error={errors[`l_cuenta_${i}`]}
                  />
                  {nat && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      marginTop: 4, fontSize: 10, fontWeight: 700,
                      padding: "2px 7px", borderRadius: 4,
                      background: sugiereDebe
                        ? "rgba(248,113,113,0.12)" : "rgba(96,165,250,0.12)",
                      color: sugiereDebe ? "#f87171" : "#60a5fa",
                      border: `1px solid ${sugiereDebe
                        ? "rgba(248,113,113,0.25)" : "rgba(96,165,250,0.25)"}`,
                      letterSpacing: "0.4px",
                    }}>
                      <i className={`fa-solid ${sugiereDebe ? "fa-arrow-left" : "fa-arrow-right"}`}
                         style={{ fontSize: 8 }}/>
                      {sugiereDebe ? "Natural: DEBE" : "Natural: HABER"}
                      {enContraNat && (
                        <span style={{ color: "#fb923c", marginLeft: 3 }}
                          title="Usando contra-naturaleza">⚡</span>
                      )}
                    </span>
                  )}
                </div>

                {/* Descripción */}
                <input type="text" placeholder="Opcional…"
                  style={inputStyle(false)}
                  value={l.descripcion}
                  onChange={(e) => setLinea(l._key, "descripcion", e.target.value)}
                />

                {/* Debe */}
                <div title={sugiereDebe ? "Lado natural de esta cuenta" : sugiereHaber ? "Contra-naturaleza para esta cuenta" : ""}>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    style={{
                      ...inputStyle(errors[`l_monto_${i}`] || errors[`l_ambos_${i}`]),
                      textAlign: "right", fontFamily: "monospace",
                      borderColor: l.debito
                        ? enContraNat && sugiereHaber
                          ? "rgba(251,146,60,0.5)"    // naranja = contra-naturaleza
                          : "rgba(248,113,113,0.4)"   // rojo suave = tiene valor
                        : undefined,
                    }}
                    value={l.debito}
                    onChange={(e) => {
                      setLinea(l._key, "debito", e.target.value);
                      if (e.target.value) setLinea(l._key, "credito", "");
                    }}
                  />
                </div>

                {/* Haber */}
                <div title={sugiereHaber ? "Lado natural de esta cuenta" : sugiereDebe ? "Contra-naturaleza para esta cuenta" : ""}>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    style={{
                      ...inputStyle(errors[`l_monto_${i}`] || errors[`l_ambos_${i}`]),
                      textAlign: "right", fontFamily: "monospace",
                      borderColor: l.credito
                        ? enContraNat && sugiereDebe
                          ? "rgba(251,146,60,0.5)"    // naranja = contra-naturaleza
                          : "rgba(96,165,250,0.4)"    // azul suave = tiene valor
                        : undefined,
                    }}
                    value={l.credito}
                    onChange={(e) => {
                      setLinea(l._key, "credito", e.target.value);
                      if (e.target.value) setLinea(l._key, "debito", "");
                    }}
                  />
                </div>

                {/* Eliminar */}
                <button type="button" onClick={() => eliminarLinea(l._key)}
                  disabled={lineas.length <= 2}
                  style={{
                    background: "none", border: "none", padding: "8px 4px",
                    color: lineas.length <= 2 ? "#364250" : "#8d98a7",
                    cursor: lineas.length <= 2 ? "default" : "pointer",
                    fontSize: 14, alignSelf: "center", transition: "color 0.2s",
                  }}
                  onMouseOver={(e) => { if (lineas.length > 2) e.currentTarget.style.color = "#f87171"; }}
                  onMouseOut={(e)  => { e.currentTarget.style.color = lineas.length <= 2 ? "#364250" : "#8d98a7"; }}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            );
          })}

          {/* Footer totales */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(180px,2fr) minmax(120px,1fr) 130px 130px 36px",
            gap: 8, padding: "10px 12px",
            background: "rgba(55,65,81,0.2)",
            borderTop: "2px solid #364250",
            alignItems: "center",
          }}>
            <button type="button" onClick={agregarLinea}
              style={{
                background: "none", border: "1px dashed #364250",
                borderRadius: 6, padding: "6px 12px",
                color: "#8d98a7", cursor: "pointer", fontSize: 13,
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = "#f97316"; e.currentTarget.style.color = "#f97316"; }}
              onMouseOut={(e)  => { e.currentTarget.style.borderColor = "#364250"; e.currentTarget.style.color = "#8d98a7"; }}
            >
              <i className="fa-solid fa-plus" style={{ marginRight: 5 }} />Agregar línea
            </button>

            <span style={{ fontSize: 12, fontWeight: 700, color: "#8d98a7",
              textTransform: "uppercase", letterSpacing: "0.4px", padding: "0 12px" }}>
              TOTALES
            </span>

            <span style={{
              textAlign: "right", fontFamily: "monospace",
              fontWeight: 700, fontSize: 14, color: "#4ade80", padding: "0 12px",
            }}>
              ₡{fmt(totalD)}
            </span>

            <span style={{
              textAlign: "right", fontFamily: "monospace",
              fontWeight: 700, fontSize: 14, color: "#60a5fa", padding: "0 12px",
            }}>
              ₡{fmt(totalC)}
            </span>
            <div />
          </div>
        </div>

        {/* ── Indicador balance ── */}
        <div style={{
          padding: "10px 14px", borderRadius: 6, fontSize: 14,
          display: "flex", alignItems: "center", gap: 10,
          background: ok ? "rgba(74,222,128,0.07)"
            : totalD === 0 ? "rgba(55,65,81,0.3)"
            : "rgba(239,68,68,0.07)",
          border: `1px solid ${ok ? "rgba(74,222,128,0.3)"
            : totalD === 0 ? "#364250"
            : "rgba(239,68,68,0.3)"}`,
        }}>
          <i className={`fa-solid ${ok ? "fa-circle-check" : totalD === 0 ? "fa-circle-info" : "fa-triangle-exclamation"}`}
            style={{ color: ok ? "#4ade80" : totalD === 0 ? "#8d98a7" : "#f87171" }} />
          <span style={{ color: ok ? "#4ade80" : totalD === 0 ? "#8d98a7" : "#f87171" }}>
            {ok ? "Asiento balanceado ✓ — listo para guardar"
              : totalD === 0 ? "Ingresá los montos"
              : `No balanceado — diferencia ₡${fmt(diff)}`}
          </span>
        </div>

        {/* ── Error API ── */}
        {apiErr && (
          <div style={{
            padding: "10px 14px", borderRadius: 6, fontSize: 14,
            background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.3)",
            color: "#f87171", display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="fa-solid fa-circle-exclamation" />{apiErr}
          </div>
        )}

        {/* ── Botones ── */}
        <div className={styles.actions} style={{ justifyContent: "flex-start" }}>
          <Link to="/finanzas/asientos" className={`${styles.btn} ${styles["btn-clean-form"]}`}
            style={{ textDecoration: "none" }}>
            Cancelar
          </Link>
          <button type="submit" disabled={saving}
            className={`${styles.btn} ${styles["btn-save"]}`}
            style={{ opacity: saving ? 0.6 : 1 }}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
              : <><i className="fa-solid fa-save" /> Guardar Asiento</>
            }
          </button>
        </div>
      </form>
    </>
  );
}
